<script lang="ts">
	import PlayIcon from "@lucide/svelte/icons/play";
	import PauseIcon from "@lucide/svelte/icons/pause";
	import SkipBackIcon from "@lucide/svelte/icons/skip-back";
	import SkipForwardIcon from "@lucide/svelte/icons/skip-forward";
	import Volume2Icon from "@lucide/svelte/icons/volume-2";
	import XIcon from "@lucide/svelte/icons/x";
	import { podcastState } from "./podcast-state.svelte";

	let { currentEpisode, isPlaying, currentTime, duration, isVisible } = $derived({
		currentEpisode: podcastState.currentEpisode,
		isPlaying: podcastState.isPlaying,
		currentTime: podcastState.currentTime,
		duration: podcastState.duration,
		isVisible: podcastState.isVisible
	});

	function handleSeek(event: Event) {
		const target = event.target as HTMLInputElement;
		podcastState.seekTo(parseFloat(target.value));
	}

	function handleVolumeChange(event: Event) {
		const target = event.target as HTMLInputElement;
		podcastState.setVolume(parseFloat(target.value));
	}
</script>

{#if isVisible && currentEpisode}
	<div class="bg-card fixed right-0 bottom-0 left-0 z-50 border-t shadow-lg">
		<div class="mx-auto max-w-5xl px-4 py-3">
			<div class="flex items-center justify-between gap-4">
				<!-- Episode Info -->
				<div class="flex min-w-0 flex-1 items-center gap-3">
					<img
						src={currentEpisode.imageUrl}
						alt={currentEpisode.channelTitle}
						class="h-10 w-10 shrink-0 rounded object-cover"
						width="40"
						height="40"
					/>
					<div class="min-w-0">
						<h4 class="text-card-foreground truncate text-sm font-semibold">
							{currentEpisode.title}
						</h4>
						<p class="text-muted-foreground truncate text-xs">
							{currentEpisode.channelTitle}
						</p>
					</div>
				</div>

				<!-- Controls -->
				<div class="flex shrink-0 items-center gap-2">
					<button
						onclick={() => podcastState.skipBackward()}
						class="text-muted-foreground hover:text-card-foreground rounded-full p-1.5 transition-colors"
					>
						<SkipBackIcon class="h-4 w-4" />
					</button>

					<button
						onclick={() => podcastState.togglePlay()}
						class="bg-primary text-primary-foreground rounded-full p-2 transition-colors hover:opacity-90"
					>
						{#if isPlaying}
							<PauseIcon class="h-4 w-4" />
						{:else}
							<PlayIcon class="ml-0.5 h-4 w-4" />
						{/if}
					</button>

					<button
						onclick={() => podcastState.skipForward()}
						class="text-muted-foreground hover:text-card-foreground rounded-full p-1.5 transition-colors"
					>
						<SkipForwardIcon class="h-4 w-4" />
					</button>
				</div>

				<!-- Progress -->
				<div class="hidden max-w-xs flex-1 items-center gap-2 md:flex">
					<span class="text-muted-foreground min-w-0 text-xs">
						{podcastState.formatTime(currentTime)}
					</span>
					<input
						type="range"
						min="0"
						max="100"
						value={podcastState.progressPercentage}
						oninput={handleSeek}
						class="podcast-slider bg-muted h-1.5 flex-1 cursor-pointer appearance-none rounded"
					/>
					<span class="text-muted-foreground min-w-0 text-xs">
						{podcastState.formatTime(duration)}
					</span>
				</div>

				<!-- Volume (desktop only) -->
				<div class="hidden items-center gap-1.5 lg:flex">
					<Volume2Icon class="text-muted-foreground h-4 w-4" />
					<input
						type="range"
						min="0"
						max="100"
						value={podcastState.volumePercentage}
						oninput={handleVolumeChange}
						class="podcast-slider bg-muted h-1.5 w-20 cursor-pointer appearance-none rounded"
					/>
				</div>

				<!-- Close -->
				<button
					onclick={() => podcastState.closePlayer()}
					class="text-muted-foreground hover:text-card-foreground shrink-0 rounded-full p-1.5 transition-colors"
				>
					<XIcon class="h-4 w-4" />
				</button>
			</div>
		</div>
	</div>
{/if}

<style>
	.podcast-slider::-webkit-slider-thumb {
		appearance: none;
		height: 14px;
		width: 14px;
		border-radius: 50%;
		background: hsl(var(--primary));
		cursor: pointer;
		border: 2px solid hsl(var(--card));
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
	}

	.podcast-slider::-moz-range-thumb {
		height: 14px;
		width: 14px;
		border-radius: 50%;
		background: hsl(var(--primary));
		cursor: pointer;
		border: 2px solid hsl(var(--card));
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
	}
</style>
